import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Filter as FilterIcon, Radio } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type CaseRow = {
  id: string;
  country: string;
  city: string | null;
  region: string | null;
  case_count: number;
  deaths: number;
  reported_date: string | null;
  summary: string | null;
};

type NewsRow = {
  id: number;
  title: string;
  url: string | null;
  source: string | null;
  published_at: string;
  is_critical: boolean;
};

const TARGET_COUNTRIES = ["Spain", "France", "United States"];

type Filter = "all" | "critical" | "cases" | "news";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "critical", label: "Critical" },
  { id: "cases", label: "Cases" },
  { id: "news", label: "News" },
];

export default function BreakingNewsBar() {
  const [items, setItems] = useState<CaseRow[]>([]);
  const [news, setNews] = useState<NewsRow[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [casesRes, newsRes] = await Promise.all([
        supabase
          .from("cases")
          .select("id,country,city,region,case_count,deaths,reported_date,summary,status")
          .in("country", TARGET_COUNTRIES)
          .eq("status", "confirmed")
          .order("reported_date", { ascending: false })
          .limit(20),
        supabase
          .from("news")
          .select("id,title,url,source,published_at,is_critical")
          .order("published_at", { ascending: false })
          .limit(15),
      ]);
      if (cancelled) return;
      if (casesRes.data) setItems(casesRes.data as CaseRow[]);
      if (newsRes.data) setNews(newsRes.data as NewsRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const formatDate = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", timeZone: "UTC" })
      : "";

  // Pinned breaking headline shown first in the ticker
  const pinned = {
    id: "pinned-bc-presumptive",
    label: "Canada",
    headline:
      "Canadian cruise passenger isolating in B.C. tests 'presumptive positive' for hantavirus",
    url: "https://www.cbc.ca/news/canada/british-columbia/bc-update-hantavirus-update-bonnie-henry-may-16-9.7202396",
    date: "2026-05-16",
  };

  const showPinned = filter === "all" || filter === "critical" || filter === "news";
  const visibleCases = filter === "all" || filter === "cases" ? items : [];
  const visibleNews =
    filter === "all" || filter === "news"
      ? news
      : filter === "critical"
        ? news.filter((n) => n.is_critical)
        : [];
  const totalCount = (showPinned ? 1 : 0) + visibleCases.length + visibleNews.length;

  return (
    <div
      role="region"
      aria-label="Breaking news ticker"
      className="relative w-full overflow-hidden border-b border-border/60 bg-[#0a0a0a] text-foreground"
    >
      {/* Subtle red glow accent */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-destructive/10 via-transparent to-destructive/10"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-destructive/70 to-transparent"
      />

      <div className="relative flex items-stretch">
        {/* Label */}
        <div className="relative flex items-center gap-2 px-3.5 py-2 bg-destructive text-destructive-foreground font-bold text-[10px] uppercase tracking-[0.22em] flex-shrink-0 shadow-[2px_0_12px_-2px_rgba(0,0,0,0.6)]">
          <span className="relative inline-flex">
            <span className="absolute inline-flex h-2 w-2 rounded-full bg-destructive-foreground/80 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-destructive-foreground" />
          </span>
          <Radio className="w-3 h-3" />
          <span>Breaking</span>
          {/* Diagonal trailing edge */}
          <span
            aria-hidden
            className="absolute right-0 top-0 h-full w-3 translate-x-full bg-destructive"
            style={{ clipPath: "polygon(0 0, 0 100%, 100% 0)" }}
          />
        </div>

        {/* Filter chips (collapsible) — diagonal trailing edge mirrors Breaking label */}
        <div className="relative hidden sm:flex items-stretch flex-shrink-0">
          <div
            className="flex items-center pl-4 pr-5 bg-white/[0.03]"
            style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 12px) 100%, 0 100%)" }}
          >
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            aria-label={filtersOpen ? "Collapse filters" : "Expand filters"}
            title={filtersOpen ? "Hide filters" : "Show filters"}
            className="flex items-center gap-1 px-1.5 py-0.5 text-muted-foreground hover:text-foreground transition-colors"
          >
            <FilterIcon className="w-2.5 h-2.5" />
            {!filtersOpen && (
              <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-destructive">
                {FILTERS.find((f) => f.id === filter)?.label}
              </span>
            )}
            {filtersOpen ? <ChevronLeft className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
          </button>
          <div
            className={[
              "flex items-center gap-0.5 overflow-hidden transition-all duration-300",
              filtersOpen ? "max-w-[240px] opacity-100 pr-1.5" : "max-w-0 opacity-0",
            ].join(" ")}
          >
            {FILTERS.map((f) => {
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  aria-pressed={active}
                  className={[
                    "px-1.5 py-[1px] rounded-[2px] text-[9px] uppercase tracking-[0.12em] font-bold border transition-colors",
                    active
                      ? "bg-destructive/15 text-destructive border-destructive/50"
                      : "bg-transparent text-muted-foreground border-border/50 hover:text-foreground hover:border-foreground/40",
                  ].join(" ")}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
          </div>
        </div>

        {/* Ticker */}
        <div className="relative flex-1 overflow-hidden">
          {/* Edge fade masks */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 top-0 z-10 h-full w-12 bg-gradient-to-r from-[#0a0a0a] to-transparent"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute right-0 top-0 z-10 h-full w-12 bg-gradient-to-l from-[#0a0a0a] to-transparent"
          />

          {totalCount === 0 ? (
            <div className="py-2 px-6 text-[12px] text-muted-foreground italic">
              No items match this filter.
            </div>
          ) : (
            <div
              key={filter}
              className="flex whitespace-nowrap py-2 animate-marquee"
              style={{ animationDuration: "16s" }}
            >
              {[0, 1].map((dup) => (
                <span key={`group-${dup}`} className="inline-flex items-center">
                  {showPinned && (
                    <a
                      key={`pinned-${dup}`}
                      href={pinned.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2.5 text-[12.5px] px-6 text-foreground hover:text-destructive transition-colors"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-destructive shadow-[0_0_8px_rgba(239,68,68,0.8)]" />
                        <span className="font-bold uppercase tracking-[0.14em] text-[10px] text-destructive">
                          {pinned.label}
                        </span>
                      </span>
                      <span className="font-semibold underline decoration-dotted underline-offset-2">
                        {pinned.headline}
                      </span>
                      <span className="text-muted-foreground font-data text-[10px] uppercase tracking-wider">
                        {formatDate(pinned.date)}
                      </span>
                      <span aria-hidden className="text-border mx-3">◆</span>
                    </a>
                  )}
                  {visibleCases.map((c) => (
                    <span
                      key={`case-${c.id}-${dup}`}
                      className="inline-flex items-center gap-2.5 text-[12.5px] px-6 text-foreground/90"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-destructive shadow-[0_0_8px_rgba(239,68,68,0.8)]" />
                        <span className="font-bold uppercase tracking-[0.14em] text-[10px] text-destructive">
                          {c.country}
                        </span>
                      </span>
                      <span className="font-medium">
                        Confirmed case
                        {c.case_count > 1 ? `s (${c.case_count})` : ""}
                        {c.city && c.city !== c.country ? ` — ${c.city}` : c.region && c.region !== c.country ? ` — ${c.region}` : ""}
                        {c.deaths > 0 ? (
                          <span className="text-destructive font-semibold">
                            {" · "}
                            {c.deaths} death{c.deaths > 1 ? "s" : ""}
                          </span>
                        ) : null}
                      </span>
                      {c.reported_date && (
                        <span className="text-muted-foreground font-data text-[10px] uppercase tracking-wider">
                          {formatDate(c.reported_date)}
                        </span>
                      )}
                      <span aria-hidden className="text-border mx-3">◆</span>
                    </span>
                  ))}
                  {visibleNews.map((n) => {
                    const safeUrl = n.url && /^https:\/\//i.test(n.url) ? n.url : null;
                    const inner = (
                      <span className="inline-flex items-center gap-2.5 text-[12.5px] px-6 text-foreground/90 hover:text-destructive transition-colors">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-destructive shadow-[0_0_8px_rgba(239,68,68,0.8)]" />
                          <span className="font-bold uppercase tracking-[0.14em] text-[10px] text-destructive">
                            {n.is_critical ? "Alert" : n.source ?? "News"}
                          </span>
                        </span>
                        <span className="font-medium">{n.title}</span>
                        <span className="text-muted-foreground font-data text-[10px] uppercase tracking-wider">
                          {formatDate(n.published_at)}
                        </span>
                        <span aria-hidden className="text-border mx-3">◆</span>
                      </span>
                    );
                    return safeUrl ? (
                      <a
                        key={`news-${n.id}-${dup}`}
                        href={safeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center"
                      >
                        {inner}
                      </a>
                    ) : (
                      <span key={`news-${n.id}-${dup}`} className="inline-flex items-center">
                        {inner}
                      </span>
                    );
                  })}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

